import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DiaryRootState } from 'appRoot/rootReducer';
import moment from 'moment';
import { communicationActions } from 'booking-creator/reducers/communication/slice';
import BookingLookupService from 'external-services/booking-lookup-service';
import { errorStateActions, getAccordionDetail } from 'app/reducers/errorState/slice';
import { CONTACT_MSG, PROBLEMS_MSG, REFRESH_MSG } from 'appRoot/app-error-messages';
import { displayActions } from 'booking-creator/reducers/display/slice';
import { rootDisplayActions } from 'app/reducers/display/slice';
import { SlidingStatesEnum } from 'app/reducers/display/types';
import { BookingMode } from 'booking-creator/reducers/display/types';
import { setPaymentUrl } from 'booking-creator/reducers/payment/slice';
import ViewActionsService from 'app/actions/view.actions';
import { BookingMethods, IBooking } from 'shared-types/SharedTypes';
import { BookingStatusNames } from 'shared-types/bookingStatusTypes';
import { updateDelayedBookings } from 'app/reducers/booking/slice';
import { quickValidationActions, saveQuickEditBooking } from 'app/reducers/quickValidation/slice';
import { useAppDispatch } from 'appRoot/Store';
import {cloneDeep} from "lodash-es";
import BookingCardCompactV2 from 'design-v2/common/BookingCardCompact/BookingCardCompactV2';
import TablesLookupService from 'external-services/tables-lookup-service/tablesLookup.service';

const FloatingBookingCard = (props: { booking: IBooking }) => {
  const { booking } = props;
  const dispatch = useAppDispatch();
  // prettier-ignore
  const fullStatusOptions = useSelector((state: DiaryRootState) => state.statusReducer.fullStatusOptions);
  // prettier-ignore
  const selectedDate = useSelector((state: DiaryRootState) => state.scheduleReducer.selectedDate);
  // prettier-ignore
  const floorplanSettings = useSelector((state: DiaryRootState) => state.floorplanSettingsReducer.floorplanSettings);
  //prettier-ignore
  const enableEndBookingTime = useSelector((state: DiaryRootState) => state.settingReducer?.generalSettings?.enableEndBookingTime);

  const handleStatusChange = async (booking: IBooking, status: BookingStatusNames) => {
    if (booking.status.name.toString() !== status.toString()) {
      await dispatch(
        updateDelayedBookings({
          delayedBooking: booking,
          changedProperty: 'status',
        })
      );
      await dispatch(
        saveQuickEditBooking({
          name: status,
          isManualUpdate: true,
          booking,
        })
      );
    }
  };

  const handlePaxSave = async (covers: number) => {
    await dispatch(
      saveQuickEditBooking({
        isManualUpdate: true,
        booking: booking,
        key: 'people',
        value: covers,
      })
    );
    dispatch(
      quickValidationActions.clearValidationAndSetBothStatuses({
        isIgnoreStaff: booking.method === BookingMethods.walkin,
      })
    );
  };

  const startTime = moment(booking.time);
  const endTime = moment(booking.bookingEnd);
  const selectedIndex = fullStatusOptions.findIndex(
    (statusItem) => statusItem.name === booking.status.name
  );

  const handleBookingClicked = useCallback(
    (activeBookingId: string) => {
      if (floorplanSettings.enableBookingCreatorOnFloorplan) {
        dispatch(communicationActions.setSmsEnabled(false));
        dispatch(communicationActions.setEmailEnabled(false));
        setTimeout(async () => {
          let activeBooking;
          try {
            activeBooking = await BookingLookupService.getById(activeBookingId, selectedDate);
          } catch (err) {
            console.warn(`error retrieving booking by id "${activeBookingId}".`);
            dispatch(
              errorStateActions.setErrorState({
                errorTitle: 'Problem opening booking',
                errorMessage: `Sorry, but we couldn't open booking with ID: ${activeBookingId}. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
                accordionDetail: getAccordionDetail(err),
              })
            );
            return null;
          }

          // Note: these should be called from src/app/reducers/booking/slice.ts -> editInBookingCreator as well
          dispatch(displayActions.setIsManualPax());
          dispatch(displayActions.setActiveBooking(activeBooking));
          dispatch(displayActions.setBackUpActiveBooking(cloneDeep(activeBooking)));
          dispatch(rootDisplayActions.setSlideState(SlidingStatesEnum.BCSlidingIn));
          dispatch(displayActions.setMode(BookingMode.viewBooking));
          dispatch(setPaymentUrl(activeBooking));
          dispatch(displayActions.setIsManualCopyOriginalBooking(false));
        }, 100);
      } else {
        setTimeout(async () => {
          let activeBooking;
          try {
            activeBooking = await BookingLookupService.getById(activeBookingId, selectedDate);
            ViewActionsService.onEditBooking({
              booking: activeBooking,
            });
          } catch (err) {
            console.warn(`error retrieving booking by id "${activeBookingId}".`);
            dispatch(
              errorStateActions.setErrorState({
                errorTitle: 'Problem opening booking',
                errorMessage: `Sorry, but we couldn't open booking with ID: ${activeBookingId}. \n\n${PROBLEMS_MSG} ${REFRESH_MSG} \n\n${CONTACT_MSG}`,
                accordionDetail: getAccordionDetail(err),
              })
            );
            return null;
          }
        }, 100);
      }
      // clearFloatingBookingCard();
    },
    [selectedDate, floorplanSettings]
  );
  const tableCapacity = useMemo(() => booking.tables.reduce((acc, t) => acc + t.capacity, 0), [booking.tables])

  return (
    <BookingCardCompactV2
      startTimeValue={startTime.format('h:mm')}
      endTimeValue={endTime.format('h:mm')}
      startTimePeriod={startTime.format('A')}
      endTimePeriod={endTime.format('A')}
      fullName={`${booking.firstName || ''} ${booking.lastName || ''}`}
      tablesNames={TablesLookupService.getTableNamesPrimaryFirst(booking.tables, booking.primaryTableId)}
      covers={booking.people}
      tags={booking.tags}
      selectedIndex={selectedIndex}
      bookingStatusList={fullStatusOptions}
      handleStatusChange={async (status: string) => {
        await handleStatusChange(booking, status as BookingStatusNames);
      }}
      handleBookingClicked={() => {
        setTimeout(() => {
          handleBookingClicked(booking._id);
        }, 100);
      }}
      isEditable={false} // forbidden for now， set it to true booking card needs to be interactive
      historicalBookingStatus={booking.status}
      handlePaxSave={handlePaxSave}
      handleChangePax={(newPax) => dispatch(quickValidationActions.setNewPax(newPax))}
      handleOpenPaxModal={() => {
        // put back if booking card needs to be interactive
        // dispatch(quickValidationActions.setOriginPax(originPax))
      }}
      handleClosePaxModal={() => {
        // put back if booking card needs to be interactive
        // dispatch(quickValidationActions.setQuickSaveAttempted(false))
        // clearFloatingBookingCard();
      }}
      isOverTableCapacity={booking.tables.length ? booking.people > tableCapacity : false}
      enableEndBookingTime={enableEndBookingTime}
    />
  );
};

export default FloatingBookingCard;
